import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Search,
  Filter,
  Edit,
  Trash2,
  Package,
  Eye,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Archive,
  CheckSquare,
  Square,
  PowerOff,
  Settings,
  Scissors,
  History,
  Power,
  CheckCircle,
  Ban,
  Lock,
  ShieldAlert
} from 'lucide-react';
import inventoryService from '../services/inventoryService';
import { hasPermission } from '../config/permissions';
import { useAuth } from '../contexts/AuthContext';
import ProductFormComplete from './ProductFormComplete';
import ProductDetailsModal from './ProductDetailsModal';
import StockByWarehouseModal from './StockByWarehouseModal';
import StockAdjustModal from './StockAdjustModal';
import SetoresManager from './SetoresManager';
import RetalhosManagerModal from './RetalhosManagerModal';
import StockKardexModal from './StockKardexModal';
import ConfirmModal from './ConfirmModal';
import toast from 'react-hot-toast';

const Inventory = () => {
  const { user, hasRole } = useAuth();
  const queryClient = useQueryClient();

  // Estados para filtros
  const [filters, setFilters] = useState({
    search: '',
    categoria_id: '',
    marca_id: '',
    ativo: '',
    low_stock: false,
    page: 1,
    limit: 20,
    sort_by: 'nome',
    sort_order: 'ASC'
  });

  const [showFilters, setShowFilters] = useState(false);
  const [showProductForm, setShowProductForm] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formMode, setFormMode] = useState('create'); // 'create' | 'edit' | 'view'
  const [showProductDetails, setShowProductDetails] = useState(false);
  const [detailsProductId, setDetailsProductId] = useState(null);
  const [detailsProductName, setDetailsProductName] = useState(null);
  const [showStockByWarehouse, setShowStockByWarehouse] = useState(false);
  const [stockWarehouseProductId, setStockWarehouseProductId] = useState(null);
  const [stockWarehouseProductName, setStockWarehouseProductName] = useState(null);
  const [showStockModal, setShowStockModal] = useState(false);
  const [stockProduct, setStockProduct] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedSetor, setSelectedSetor] = useState(null);
  const [showSetoresManager, setShowSetoresManager] = useState(false);
  const [showRetalhosModal, setShowRetalhosModal] = useState(false);
  const [kardexProduct, setKardexProduct] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmBulkInactivate, setConfirmBulkInactivate] = useState(false);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [forceDeleteProductData, setForceDeleteProductData] = useState(null);
  const [managerPassword, setManagerPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const searchTimeoutRef = useRef(null);

  const isManager = ['GERENCIA', 'ADMIN', 'CEO'].includes(user?.role) || hasRole(['GERENCIA', 'ADMIN', 'CEO']) || hasPermission(user, 'all:full');

  // Queries
  const { data: productsData, isLoading, error } = useQuery({
    queryKey: ['inventory-products', filters],
    queryFn: () => inventoryService.getProducts(filters),
    placeholderData: (previousData) => previousData
  });

  const { data: categoriesData } = useQuery({
    queryKey: ['inventory-categories'],
    queryFn: inventoryService.getCategories
  });

  const { data: brandsData } = useQuery({
    queryKey: ['inventory-brands'],
    queryFn: inventoryService.getBrands
  });

  const { data: statsData } = useQuery({
    queryKey: ['inventory-stats'],
    queryFn: inventoryService.getStats
  });

  const { data: setoresData } = useQuery({
    queryKey: ['inventory-setores'],
    queryFn: () => inventoryService.getSetores({ ativo: true })
  });

  // Mutations
  const toggleProductStatusMutation = useMutation({
    mutationFn: ({ id, ativo }) => inventoryService.updateProduct(id, { ativo }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(['inventory-products']);
      queryClient.invalidateQueries(['inventory-stats']);
      toast.success(variables.ativo ? 'Produto ativado com sucesso!' : 'Produto inativado com sucesso!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao alterar status do produto');
    }
  });

  const forceDeleteProductMutation = useMutation({
    mutationFn: ({ id, password }) => inventoryService.forceDeleteProduct(id, password),
    onSuccess: (data) => {
      queryClient.invalidateQueries(['inventory-products']);
      queryClient.invalidateQueries(['inventory-stats']);
      toast.success(data?.message || 'Produto e todos os registros vinculados foram excluídos definitivamente!');
      setForceDeleteProductData(null);
      setManagerPassword('');
      setPasswordError('');
    },
    onError: (error) => {
      const msg = error.response?.data?.message || 'Erro ao excluir produto permanentemente';
      setPasswordError(msg);
      toast.error(msg);
    }
  });

  const deleteProductMutation = useMutation({
    mutationFn: inventoryService.deleteProduct,
    onSuccess: (_, deletedId) => {
      queryClient.invalidateQueries(['inventory-products']);
      queryClient.invalidateQueries(['inventory-stats']);
      toast.success('Produto inativado com sucesso!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao inativar produto');
    }
  });

  const bulkInactivateMutation = useMutation({
    mutationFn: (ids) => Promise.all(ids.map(id => inventoryService.updateProduct(Number(id), { ativo: false }))),
    onSuccess: () => {
      queryClient.invalidateQueries(['inventory-products']);
      queryClient.invalidateQueries(['inventory-stats']);
      toast.success('Produtos inativados com sucesso!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao inativar produtos');
    }
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: inventoryService.deleteProductsBatch,
    onSuccess: (result) => {
      queryClient.invalidateQueries(['inventory-products']);
      queryClient.invalidateQueries(['inventory-stats']);
      if (result.success) {
        toast.success(result.message);
      } else {
        toast.error(result.message);
      }
    },
    onError: (error) => {
      const msg = error.response?.data?.message || 'Erro ao excluir produtos';
      toast.error(msg);
    }
  });

  // Handlers
  const handleFilterChange = (key, value) => {
    setFilters(prev => {
      const newFilters = {
        ...prev,
        [key]: value,
        page: 1
      };
      return newFilters;
    });
  };

  // Sync selectedSetor with filters - use useCallback-free approach
  useEffect(() => {
    setFilters(prev => {
      if (prev.setor_id === (selectedSetor || '') && prev.page === 1) return prev;
      return {
        ...prev,
        setor_id: selectedSetor || '',
        page: 1
      };
    });
  }, [selectedSetor]);

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const handleSort = (field) => {
    setFilters(prev => ({
      ...prev,
      sort_by: field,
      sort_order: prev.sort_by === field && prev.sort_order === 'ASC' ? 'DESC' : 'ASC'
    }));
  };

  const handleDeleteProduct = (id, nome) => {
    setConfirmDelete({ id, nome });
  };

  const handleConfirmDelete = () => {
    if (confirmDelete?.id) {
      deleteProductMutation.mutate(confirmDelete.id);
    }
    setConfirmDelete(null);
  };

  const handleOpenStockModal = (product) => {
    setStockProduct(product);
    setShowStockModal(true);
  };

  const handleViewProduct = (product) => {
    setDetailsProductId(product.id);
    setDetailsProductName(product.nome);
    setShowProductDetails(true);
  };

  const handleEditProduct = (product) => {
    setSelectedProduct(product);
    setFormMode('edit');
    setShowProductForm(true);
  };

  const handleViewStockByWarehouse = (productId, productName) => {
    setStockWarehouseProductId(productId);
    setStockWarehouseProductName(productName);
    setShowStockByWarehouse(true);
  };

  const handleOpenStockFromDetails = () => {
    setShowProductDetails(false);
    const product = {
      id: detailsProductId,
      nome: detailsProductName
    };
    setStockProduct(product);
    setShowStockModal(true);
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      categoria_id: '',
      marca_id: '',
      ativo: '',
      low_stock: false,
      page: 1,
      limit: 20,
      sort_by: 'nome',
      sort_order: 'ASC'
    });
    setSelectedSetor(null);
    setSearchInput('');
  };

  // Bulk actions
  const toggleSelectProduct = (id) => {
    setSelectedProducts(prev =>
      prev.includes(id)
        ? prev.filter(p => p !== id)
        : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    const products = productsData?.data || [];
    if (selectedProducts.length === products.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(products.map(p => p.id));
    }
  };

  const handleBulkInactivate = async () => {
    if (selectedProducts.length === 0) {
      toast.error('Nenhum produto selecionado');
      return;
    }
    setConfirmBulkInactivate(true);
  };

  const handleConfirmBulkInactivate = async () => {
    setConfirmBulkInactivate(false);
    bulkInactivateMutation.mutate(selectedProducts);
    setSelectedProducts([]);
  };

  const handleBulkDelete = async () => {
    if (selectedProducts.length === 0) {
      toast.error('Nenhum produto selecionado');
      return;
    }
    setConfirmBulkDelete(true);
  };

  const handleConfirmBulkDelete = async () => {
    setConfirmBulkDelete(false);
    bulkDeleteMutation.mutate(selectedProducts);
    setSelectedProducts([]);
  };

  // Verificar permissões
  const canManage = hasPermission(user?.role, 'inventory:full');
  const canView = hasPermission(user?.role, 'inventory:view') || hasPermission(user?.role, 'inventory:full');

  if (!canView) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
            <p className="text-red-800">Você não tem permissão para acessar o módulo de estoque.</p>
          </div>
        </div>
      </div>
    );
  }

  const products = productsData?.data || [];
  const pagination = productsData?.pagination || {};

  const formatQuantity = (value) => {
    const num = parseFloat(value) || 0;
    return num.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  };

  return (
    <div className="p-3 sm:p-4 lg:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-0 mb-4 sm:mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Estoque</h1>
          <p className="text-sm sm:text-base text-gray-600">Gerencie produtos, variantes e estoque</p>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <button
              onClick={() => setShowRetalhosModal(true)}
              className="bg-amber-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-amber-700 flex items-center justify-center gap-2 text-sm sm:text-base font-medium shadow-sm transition-colors"
              title="Gerenciar sobras e retalhos com código único e QR Code"
            >
              <Scissors className="h-4 w-4" />
              <span className="hidden sm:inline">Sobras / Retalhos MDF</span>
              <span className="sm:hidden">Retalhos</span>
            </button>
            <button
              onClick={() => setShowSetoresManager(true)}
              className="bg-gray-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-gray-700 flex items-center justify-center gap-2 text-sm sm:text-base"
            >
              <Settings className="h-4 w-4" />
              <span className="hidden sm:inline">Gerenciar Setores</span>
            </button>
            <button
              onClick={() => {
                setSelectedProduct(null);
                setFormMode('create');
                setShowProductForm(true);
              }}
              className="bg-primary-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-primary-700 flex items-center justify-center gap-2 text-sm sm:text-base"
            >
              <Plus className="h-4 w-4" />
              <span className="sm:inline">Novo Produto</span>
            </button>
          </div>
        )}
      </div>

      {/* Estatísticas */}
      {statsData?.data && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div className="bg-white p-3 sm:p-4 rounded-lg shadow">
            <div className="flex items-center">
              <Package className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600 flex-shrink-0" />
              <div className="ml-2 sm:ml-3 min-w-0">
                <p className="text-xs sm:text-sm font-medium text-gray-500 truncate">Total de Produtos</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900">{statsData.data.total_products}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-3 sm:p-4 rounded-lg shadow">
            <div className="flex items-center">
              <TrendingUp className="h-6 w-6 sm:h-8 sm:w-8 text-green-600 flex-shrink-0" />
              <div className="ml-2 sm:ml-3 min-w-0">
                <p className="text-xs sm:text-sm font-medium text-gray-500 truncate">Total de Variantes</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900">{statsData.data.total_variants}</p>
              </div>
            </div>
          </div>
          
          <div
            className={`bg-white p-3 sm:p-4 rounded-lg shadow cursor-pointer hover:bg-gray-50 transition-colors ${filters.low_stock ? 'ring-2 ring-yellow-500' : ''}`}
            onClick={() => handleFilterChange('low_stock', !filters.low_stock)}
          >
            <div className="flex items-center">
              <TrendingDown className="h-6 w-6 sm:h-8 sm:w-8 text-yellow-600 flex-shrink-0" />
              <div className="ml-2 sm:ml-3 min-w-0">
                <p className="text-xs sm:text-sm font-medium text-gray-500 truncate">Estoque Baixo</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900">{statsData.data.low_stock_products}</p>
              </div>
            </div>
          </div>

          {hasRole(['GERENCIA']) && (
            <div className="bg-white p-3 sm:p-4 rounded-lg shadow">
              <div className="flex items-center">
                <Package className="h-6 w-6 sm:h-8 sm:w-8 text-purple-600 flex-shrink-0" />
                <div className="ml-2 sm:ml-3 min-w-0">
                  <p className="text-xs sm:text-sm font-medium text-gray-500 truncate">Valor Total</p>
                  <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
                    R$ {statsData.data.total_value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">(baseado no preço de custo atual das variantes)</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Setores Tabs */}
      <div className="bg-white rounded-lg shadow mb-4 sm:mb-6">
        <div className="flex flex-wrap gap-1 sm:gap-2 p-2 border-b">
          <button
            onClick={() => setSelectedSetor(null)}
            className={`px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedSetor === null
                ? 'bg-primary-100 text-primary-700 border-b-2 border-primary-500'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            Todos
          </button>
          {setoresData?.data?.map((setor) => (
            <button
              key={setor.id}
              onClick={() => setSelectedSetor(setor.id)}
              className={`px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedSetor === setor.id
                  ? 'bg-primary-100 text-primary-700 border-b-2 border-primary-500'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {setor.nome}
            </button>
          ))}
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow mb-4 sm:mb-6">
        <div className="p-3 sm:p-4 border-b">
          <div className="flex justify-between items-center">
            <h3 className="text-base sm:text-lg font-medium">Filtros</h3>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-1 sm:gap-2 text-gray-600 hover:text-gray-900 text-sm sm:text-base"
            >
              <Filter className="h-4 w-4" />
              <span className="hidden sm:inline">{showFilters ? 'Ocultar' : 'Mostrar'} Filtros</span>
              <span className="sm:hidden">{showFilters ? 'Fechar' : 'Filtros'}</span>
            </button>
          </div>
        </div>
        
        {showFilters && (
          <div className="p-3 sm:p-4 space-y-3 sm:space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {/* Busca */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Buscar
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchInput}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSearchInput(value);
                      if (searchTimeoutRef.current) {
                        clearTimeout(searchTimeoutRef.current);
                      }
                      searchTimeoutRef.current = setTimeout(() => {
                        handleFilterChange('search', value);
                      }, 300);
                    }}
                    placeholder="Nome, SKU ou código..."
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Categoria */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Categoria
                </label>
                <select
                  value={filters.categoria_id}
                  onChange={(e) => handleFilterChange('categoria_id', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Todas as categorias</option>
                  {Array.isArray(categoriesData?.data) && categoriesData.data.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.nome}
                    </option>
                  ))}
                </select>
              </div>

              {/* Marca */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Marca
                </label>
                <select
                  value={filters.marca_id}
                  onChange={(e) => handleFilterChange('marca_id', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Todas as marcas</option>
                  {Array.isArray(brandsData?.data) && brandsData.data.map(brand => (
                    <option key={brand.id} value={brand.id}>
                      {brand.nome}
                    </option>
                  ))}
                </select>
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={filters.ativo}
                  onChange={(e) => handleFilterChange('ativo', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Todos</option>
                  <option value="true">Ativo</option>
                  <option value="false">Inativo</option>
                </select>
              </div>
            </div>

            <div className="flex justify-between">
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-gray-600 hover:text-gray-900"
              >
                Limpar Filtros
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Tabela de Produtos */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Carregando produtos...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <p className="text-red-600">Erro ao carregar produtos</p>
          </div>
        ) : products.length === 0 ? (
          <div className="p-8 text-center">
            <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Nenhum produto encontrado</p>
          </div>
        ) : (
          <>
            {/* Tabela responsiva - scroll horizontal em mobile */}
            <div className="overflow-x-auto -mx-4 sm:mx-0">
              <div className="inline-block min-w-full align-middle">
                <div className="overflow-hidden">
                  {/* Bulk Actions */}
                  {selectedProducts.length > 0 && canManage && (
                    <div className="flex gap-2 mb-4 p-3 bg-orange-50 rounded-lg border border-orange-200">
                      <span className="text-sm text-orange-800 font-medium self-center">
                        {selectedProducts.length} selecionado(s)
                      </span>
                      <button
                        onClick={handleBulkInactivate}
                        className="flex items-center gap-2 px-3 py-1.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 text-sm"
                      >
                        <PowerOff className="w-4 h-4" />
                        Inativar
                      </button>
                      <button
                        onClick={handleBulkDelete}
                        className="flex items-center gap-2 px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                      >
                        <Trash2 className="w-4 h-4" />
                        Excluir
                      </button>
                    </div>
                  )}

                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 sm:px-4 py-3 text-left w-10">
                          <button
                            onClick={toggleSelectAll}
                            className="text-gray-500 hover:text-gray-700"
                            title={selectedProducts.length === products.length ? "Desmarcar todos" : "Selecionar todos"}
                          >
                            {selectedProducts.length === products.length && products.length > 0 ? (
                              <CheckSquare className="w-5 h-5" />
                            ) : (
                              <Square className="w-5 h-5" />
                            )}
                          </button>
                        </th>
                        {canManage && (
                          <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Ações
                          </th>
                        )}
                        <th
                          className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 w-48"
                          onClick={() => handleSort('nome')}
                        >
                          <div className="flex items-center">
                            <span>Produto</span>
                            {filters.sort_by === 'nome' && (
                              <span className="ml-1">
                                {filters.sort_order === 'ASC' ? '↑' : '↓'}
                              </span>
                            )}
                          </div>
                        </th>
                        <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                          Categoria
                        </th>
                        <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                          Marca
                        </th>
                        <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Variantes
                        </th>
                        <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                          Estoque
                        </th>
                        <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {products.map((product) => (
                        <tr key={product.id} className={`hover:bg-gray-50 ${selectedProducts.includes(product.id) ? 'bg-blue-50' : ''}`}>
                          <td className="px-3 sm:px-4 py-4">
                            <button
                              onClick={() => toggleSelectProduct(product.id)}
                              className="text-gray-500 hover:text-blue-600"
                            >
                              {selectedProducts.includes(product.id) ? (
                                <CheckSquare className="w-5 h-5 text-blue-600" />
                              ) : (
                                <Square className="w-5 h-5" />
                              )}
                            </button>
                          </td>
                          {canManage && (
                            <td className="px-3 sm:px-4 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex items-center gap-1 sm:gap-2">
                                <button
                                  onClick={() => handleViewProduct(product)}
                                  className="text-blue-600 hover:text-blue-900 p-1"
                                  title="Ver detalhes"
                                >
                                  <Eye className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleEditProduct(product)}
                                  className="text-indigo-600 hover:text-indigo-900 p-1"
                                  title="Editar"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleOpenStockModal(product)}
                                  className="text-amber-600 hover:text-amber-800 p-1"
                                  title="Ajustar estoque"
                                >
                                  <Archive className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => setKardexProduct(product)}
                                  className="text-blue-600 hover:text-blue-800 p-1"
                                  title="Extrato Kardex de Movimentações"
                                >
                                  <History className="h-4 w-4" />
                                </button>

                                {/* Botão Rápido de Ativar / Inativar */}
                                <button
                                  type="button"
                                  onClick={() => toggleProductStatusMutation.mutate({ id: product.id, ativo: !product.ativo })}
                                  disabled={toggleProductStatusMutation.isPending}
                                  className={`p-1 rounded transition-colors ${
                                    product.ativo
                                      ? 'text-amber-600 hover:text-amber-800 hover:bg-amber-50'
                                      : 'text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50'
                                  }`}
                                  title={product.ativo ? 'Inativar produto' : 'Reativar produto'}
                                >
                                  <Power className="h-4 w-4" />
                                </button>

                                {/* Botão de Exclusão: Exclusão Definitiva para Gerente (com senha) ou Inativação */}
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (isManager) {
                                      setForceDeleteProductData({ id: product.id, nome: product.nome });
                                      setManagerPassword('');
                                      setPasswordError('');
                                    } else {
                                      handleDeleteProduct(product.id, product.nome);
                                    }
                                  }}
                                  className="text-red-600 hover:text-red-900 hover:bg-red-50 p-1 rounded transition-colors"
                                  title={isManager ? 'Excluir definitivamente (requer senha)' : 'Inativar produto'}
                                  disabled={deleteProductMutation.isPending || forceDeleteProductMutation.isPending}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </td>
                          )}
                          <td className="px-3 sm:px-6 py-4">
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-gray-900 truncate">
                                {product.nome}
                              </div>
                              <div className="text-xs text-gray-500 truncate">
                                SKU: {product.sku || '-'}
                              </div>
                            </div>
                          </td>
                          <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 hidden sm:table-cell">
                            {product.categoria_nome || '-'}
                          </td>
                          <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 hidden md:table-cell">
                            {product.marca_nome || '-'}
                          </td>
                          <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div className="flex items-center gap-1 sm:gap-2">
                              <span>{product.variantes_ativas}</span>
                              <span className="text-gray-400">/</span>
                              <span className="text-gray-500">{product.total_variantes}</span>
                            </div>
                          </td>
                          <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm hidden lg:table-cell">
                            <button
                              onClick={() => handleViewStockByWarehouse(product.id, product.nome)}
                              className={`text-left hover:text-primary-600 font-medium ${
                                parseFloat(product.estoque_total || 0) <= 0 ? 'text-red-600' : 'text-gray-900'
                              }`}
                              title="Ver detalhes de estoque por armazém"
                            >
                              {formatQuantity(product.estoque_total)}
                            </button>
                          </td>
                          <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                            <button
                              type="button"
                              onClick={() => canManage && toggleProductStatusMutation.mutate({ id: product.id, ativo: !product.ativo })}
                              disabled={!canManage || toggleProductStatusMutation.isPending}
                              className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full border transition-all cursor-pointer shadow-2xs ${
                                product.ativo
                                  ? 'bg-green-100 text-green-800 border-green-200 hover:bg-green-200'
                                  : 'bg-red-100 text-red-800 border-red-200 hover:bg-red-200'
                              }`}
                              title={canManage ? (product.ativo ? 'Clique para inativar este produto' : 'Clique para reativar este produto') : undefined}
                            >
                              <span className={`w-1.5 h-1.5 rounded-full ${product.ativo ? 'bg-green-600 animate-pulse' : 'bg-red-600'}`} />
                              <span>{product.ativo ? 'Ativo' : 'Inativo'}</span>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Paginação */}
            {pagination.pages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page >= pagination.pages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Próximo
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Mostrando{' '}
                      <span className="font-medium">
                        {(pagination.page - 1) * pagination.limit + 1}
                      </span>{' '}
                      até{' '}
                      <span className="font-medium">
                        {Math.min(pagination.page * pagination.limit, pagination.total)}
                      </span>{' '}
                      de{' '}
                      <span className="font-medium">{pagination.total}</span>{' '}
                      resultados
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      {(() => {
                        const current = pagination.page;
                        const total = pagination.pages;
                        const pages = [];
                        const delta = 2;

                        if (total <= 7) {
                          for (let i = 1; i <= total; i++) pages.push(i);
                        } else {
                          pages.push(1);

                          if (current - delta > 2) {
                            pages.push('...');
                          }

                          const start = Math.max(2, current - delta);
                          const end = Math.min(total - 1, current + delta);

                          for (let i = start; i <= end; i++) {
                            if (!pages.includes(i)) pages.push(i);
                          }

                          if (current + delta < total - 1) {
                            pages.push('...');
                          }

                          if (!pages.includes(total)) {
                            pages.push(total);
                          }
                        }

                        return pages.map((page, index) => {
                          if (page === '...') {
                            return (
                              <span
                                key={`ellipsis-${index}`}
                                className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-gray-500"
                              >
                                ...
                              </span>
                            );
                          }
                          return (
                            <button
                              key={page}
                              onClick={() => handlePageChange(page)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                page === current
                                  ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {page}
                            </button>
                          );
                        });
                      })()}
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal de Cadastro de Produto */}
      {showProductForm && (
        <ProductFormComplete
          product={selectedProduct}
          mode={formMode}
          onClose={() => {
            setShowProductForm(false);
            setSelectedProduct(null);
            setFormMode('create');
          }}
          onSuccess={() => {
            setShowProductForm(false);
            setSelectedProduct(null);
            setFormMode('create');
          }}
        />
      )}

      {/* Modal de Ajuste de Estoque */}
      {showStockModal && stockProduct && (
        <StockAdjustModal
          productId={stockProduct.id}
          productName={stockProduct.nome}
          onClose={() => {
            setShowStockModal(false);
            setStockProduct(null);
          }}
        />
      )}

      {/* Modal de Detalhes do Produto */}
      {showProductDetails && detailsProductId && (
        <ProductDetailsModal
          productId={detailsProductId}
          productName={detailsProductName}
          isOpen={showProductDetails}
          onClose={() => {
            setShowProductDetails(false);
            setDetailsProductId(null);
            setDetailsProductName(null);
          }}
          onViewStock={handleViewStockByWarehouse}
        />
      )}

      {/* Modal de Estoque por Armazém */}
      {showStockByWarehouse && stockWarehouseProductId && (
        <StockByWarehouseModal
          productId={stockWarehouseProductId}
          productName={stockWarehouseProductName}
          isOpen={showStockByWarehouse}
          onClose={() => {
            setShowStockByWarehouse(false);
            setStockWarehouseProductId(null);
            setStockWarehouseProductName(null);
          }}
        />
      )}

      {/* Modal de Gerenciar Setores */}
      {showSetoresManager && (
        <SetoresManager
          onClose={() => {
            setShowSetoresManager(false);
            queryClient.invalidateQueries(['inventory-setores']);
          }}
        />
      )}

      {/* Confirm Delete Modal */}
      {confirmDelete && (
        <ConfirmModal
          isOpen={!!confirmDelete}
          title="Confirmar Exclusão"
          message={`Tem certeza que deseja deletar o produto "${confirmDelete.nome}"?`}
          onConfirm={handleConfirmDelete}
          onCancel={() => setConfirmDelete(null)}
          confirmText="Deletar"
          cancelText="Cancelar"
          danger
        />
      )}

      {/* Confirm Bulk Inactivate Modal */}
      <ConfirmModal
        isOpen={confirmBulkInactivate}
        title="Confirmar Inativação"
        message={`Tem certeza que deseja inativar ${selectedProducts.length} produto(s)?`}
        onConfirm={handleConfirmBulkInactivate}
        onCancel={() => setConfirmBulkInactivate(false)}
        confirmText="Inativar"
        cancelText="Cancelar"
      />

      {/* Confirm Bulk Delete Modal */}
      <ConfirmModal
        isOpen={confirmBulkDelete}
        title="ATENÇÃO: Exclusão Definitiva"
        message={`${selectedProducts.length} produto(s) será(ão) removido(s) permanentemente. Continuar?`}
        onConfirm={handleConfirmBulkDelete}
        onCancel={() => setConfirmBulkDelete(false)}
        confirmText="Excluir"
        cancelText="Cancelar"
        danger
      />

      {/* Modal de Gestão de Retalhos e Sobras com QR Code */}
      {showRetalhosModal && (
        <RetalhosManagerModal
          isOpen={showRetalhosModal}
          onClose={() => setShowRetalhosModal(false)}
        />
      )}

      {/* Modal de Extrato Kardex */}
      {kardexProduct && (
        <StockKardexModal
          productId={kardexProduct.id}
          productName={kardexProduct.nome}
          isOpen={!!kardexProduct}
          onClose={() => setKardexProduct(null)}
        />
      )}

      {/* Modal Seguro de Exclusão Permanente (Requer Senha do Gerente) */}
      {forceDeleteProductData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-xs p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-red-200 animate-scaleIn">
            <div className="bg-red-50 p-5 border-b border-red-100 flex items-center gap-3">
              <div className="p-2.5 bg-red-100 text-red-700 rounded-xl">
                <ShieldAlert className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-base font-bold text-red-950">Exclusão Definitiva de Produto</h3>
                <p className="text-xs text-red-700 mt-0.5">Ação restrita à Gerência • Irreversível</p>
              </div>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!managerPassword) {
                  setPasswordError('Digite sua senha para confirmar');
                  return;
                }
                forceDeleteProductMutation.mutate({
                  id: forceDeleteProductData.id,
                  password: managerPassword
                });
              }}
              className="p-5 space-y-4"
            >
              <div className="bg-red-50/60 border border-red-200 rounded-xl p-3.5 text-xs text-red-900 space-y-1.5">
                <p className="font-bold flex items-center gap-1.5 text-red-950">
                  <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
                  <span>Atenção: Todos os dados serão apagados</span>
                </p>
                <p className="text-red-800 leading-relaxed">
                  O produto <strong className="text-red-950">"{forceDeleteProductData.nome}"</strong> (ID #{forceDeleteProductData.id}), todas as suas variantes e todo o estoque relacionado serão excluídos permanentemente do banco de dados.
                </p>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                  <Lock className="w-3.5 h-3.5 text-gray-500" />
                  <span>Senha de Login do Gerente *</span>
                </label>
                <input
                  type="password"
                  required
                  autoFocus
                  value={managerPassword}
                  onChange={(e) => {
                    setManagerPassword(e.target.value);
                    if (passwordError) setPasswordError('');
                  }}
                  placeholder="Digite sua senha de login..."
                  className={`w-full px-3.5 py-2.5 bg-white border rounded-xl text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 ${
                    passwordError ? 'border-red-400 bg-red-50/20' : 'border-gray-300'
                  }`}
                  disabled={forceDeleteProductMutation.isPending}
                />
                {passwordError && (
                  <p className="text-xs text-red-600 mt-1 font-medium">{passwordError}</p>
                )}
              </div>

              <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => {
                    setForceDeleteProductData(null);
                    setManagerPassword('');
                    setPasswordError('');
                  }}
                  disabled={forceDeleteProductMutation.isPending}
                  className="px-4 py-2 text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={forceDeleteProductMutation.isPending || !managerPassword}
                  className="px-5 py-2 text-xs font-bold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-xl shadow-sm transition-all flex items-center gap-1.5"
                >
                  {forceDeleteProductMutation.isPending ? (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Excluindo...</span>
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-3.5 h-3.5" />
                      <span>Confirmar Exclusão Permanente</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Inventory;